"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import type { JobStatus, Project, VideoJob } from "@easyshot/shared";
import {
  AVATARS,
  PLATFORM_PRESETS,
  RATIO_CSS,
  STYLE_OPTIONS,
} from "@easyshot/shared";
import { api } from "@/lib/api";
import { Badge, Button, Card, Progress, Spinner, cn } from "./ui";
import { useCurrency } from "./currency-provider";
import { MIN_VIDEO_PRICE_CENTS } from "@/lib/pricing";
import { ProjectCover } from "./project-cover";

const STATUS: Record<
  JobStatus,
  { label: string; tone: "neutral" | "accent" | "success" | "warning" | "danger" }
> = {
  queued: { label: "Queued", tone: "neutral" },
  scripting: { label: "Voice synth", tone: "warning" },
  rendering: { label: "Rendering", tone: "warning" },
  succeeded: { label: "Ready", tone: "success" },
  failed: { label: "Failed", tone: "danger" },
};

function styleLabel(style: string): string {
  if (style === "minimax_h3_product_video") return "MiniMax H3 Product Video";
  if (style === "infinitetalk_avatar" || style === "clone_creator") return "Spokesperson";
  return STYLE_OPTIONS.find((option) => option.id === style)?.label ?? style;
}

export function ProjectDetail({ id }: { id: string }) {
  const router = useRouter();
  const [project, setProject] = useState<Project | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [returningToCreate, setReturningToCreate] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setProject(await api.getProject(id));
    } catch {
      setMissing(true);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = project?.jobs.some(
    (j) => j.status !== "succeeded" && j.status !== "failed",
  );

  // 有任务没跑完就持续轮询
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => void load(), 1500);
    return () => clearInterval(t);
  }, [pending, load]);

  async function regenerate() {
    if (!project) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.generate(project.id, {
        platforms: project.platforms,
        variants: 1,
        varyHooks: true,
        varyAvatars: true,
      });
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Generation failed");
    } finally {
      setBusy(false);
    }
  }

  async function returnToPreviousStep() {
    if (!project) return;
    setReturningToCreate(true);
    setActionError(null);
    try {
      const saved = await api.saveDraft({
        name: project.name,
        step: project.script ? 3 : 2,
        payload: {
          url: "",
          text: project.product.description,
          product: project.product,
          style: project.style,
          platforms: project.platforms,
          avatarId: project.avatarId,
          voiceId: project.voiceId,
          infiniteTalkBackground: project.product.backgroundMedia ? [project.product.backgroundMedia] : [],
          removeAvatarBackground: project.product.removeAvatarBackground ?? false,
          script: project.script ?? null,
          media: [],
          variants: 1,
          varyHooks: true,
          varyAvatars: false,
          stylePicked: true,
        },
      });
      router.push(`/studio?draft=${encodeURIComponent(saved.id)}`);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : "Could not return to Create",
      );
      setReturningToCreate(false);
    }
  }

  async function rename() {
    if (!project) return;
    const name = window.prompt("Project name", project.name)?.trim();
    if (!name || name === project.name) return;
    try {
      setProject(await api.renameProject(project.id, name));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Rename failed");
    }
  }

  async function removeProject() {
    if (!project) return;
    if (!window.confirm(`Delete "${project.name}" and all its videos?`)) return;
    setBusy(true);
    try {
      await api.deleteProject(project.id);
      router.push("/studio/projects");
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Delete failed");
      setBusy(false);
    }
  }

  if (missing) {
    return (
      <div className="mx-auto max-w-5xl px-5 py-20 text-center">
        <h1 className="text-lg font-semibold">Project not found</h1>
        <p className="mt-2 text-sm text-muted">
          Demo data lives in server memory and clears on restart.
        </p>
        <Link
          href="/studio"
          className="mt-6 inline-block text-sm text-[var(--accent-2)] hover:underline"
        >
          Create a new one →
        </Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  const done = project.jobs.filter((j) => j.status === "succeeded").length;

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
      <Link
        href="/studio/projects"
        className="text-[13px] text-muted transition hover:text-foreground"
      >
        ← Projects
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-5">
        <div className="flex min-w-0 gap-4">
          <div className="shrink-0 text-center">
            <ProjectCover project={project} className="size-24" />
            <div className="mt-1 text-[10px] text-muted">Video cover</div>
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold tracking-tight">
              {project.name}
              <button
                type="button"
                onClick={() => void rename()}
                title="Rename project"
                className="ml-2 align-middle text-sm text-muted transition hover:text-foreground"
              >
                ✎
              </button>
            </h1>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge tone="accent">
                {styleLabel(project.style)}
              </Badge>
              <Badge>
                {AVATARS.find((a) => a.id === project.avatarId)?.name ??
                  (project.avatarId === "av_none" ? "No avatar" : "Customer avatar")}
              </Badge>
              {project.platforms.map((p) => (
                <Badge key={p}>
                  {PLATFORM_PRESETS.find((x) => x.id === p)?.label}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => void returnToPreviousStep()}
            disabled={busy || returningToCreate}
            size="sm"
          >
            {returningToCreate ? <Spinner /> : "←"} Back to previous step
          </Button>
          <Button
            variant="secondary"
            onClick={() => void removeProject()}
            disabled={busy || returningToCreate}
            size="sm"
            className="text-[var(--danger)]"
          >
            Delete
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowScript((v) => !v)}
            size="sm"
          >
            {showScript ? "Hide script" : "View script"}
          </Button>
          <Button onClick={() => void regenerate()} disabled={busy} size="sm">
            {busy ? <Spinner /> : "＋"} Generate one more
          </Button>
        </div>
      </div>

      {actionError && (
        <div className="mt-4 rounded-xl border border-[var(--danger)]/40 bg-[var(--danger)]/10 px-4 py-3 text-[13px] text-[var(--danger)]">
          {actionError}
        </div>
      )}

      {showScript && project.script && (
        <Card className="mt-6 p-5">
          <div className="mb-3 flex items-center gap-2">
            <Badge tone="accent">Hook</Badge>
            <span className="text-sm">{project.script.hook}</span>
          </div>
          <ol className="space-y-2.5">
            {project.script.scenes.map((s, i) => (
              <li key={s.id} className="flex gap-3 text-[13px]">
                <span className="w-6 shrink-0 font-mono text-muted">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1">{s.narration}</span>
                <span className="shrink-0 font-mono text-[11px] text-muted">
                  {s.durationSec}s
                </span>
              </li>
            ))}
          </ol>
        </Card>
      )}

      <div className="mt-8 mb-4 flex items-center justify-between text-sm">
        <span className="font-medium">
          {project.jobs.length} videos
          {done > 0 && (
            <span className="ml-2 text-[13px] font-normal text-muted">
              {done} ready
            </span>
          )}
        </span>
        {pending && (
          <span className="flex items-center gap-2 text-[13px] text-[var(--warning)]">
            <Spinner className="size-3.5 border-[var(--warning)]/30 border-t-[var(--warning)]" />
            Rendering — refreshes automatically
          </span>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {project.jobs.map((job) => (
          <JobCard key={job.id} job={job} onChanged={() => void load()} />
        ))}
      </div>

      {project.jobs.length === 0 && (
        <Card className="px-6 py-14 text-center text-sm text-muted">
          No render jobs yet
        </Card>
      )}
    </div>
  );
}

function JobCard({
  job,
  onChanged,
}: {
  job: VideoJob;
  onChanged: () => void;
}) {
  const { currency, formatUsdCents } = useCurrency();
  const s = STATUS[job.status];
  const platform = PLATFORM_PRESETS.find((p) => p.id === job.platform);
  const avatar = AVATARS.find((a) => a.id === job.avatarId);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [checkingImage, setCheckingImage] = useState(false);
  const [imageCheck, setImageCheck] = useState<string | null>(null);
  const [outputMetadata, setOutputMetadata] = useState<{ width: number; height: number; duration: number } | null>(null);

  async function checkImage() {
    setCheckingImage(true);
    setImageCheck(null);
    try {
      const result = await api.checkJobImage(job.id);
      setImageCheck(`Image checked and saved to private storage (${result.width} × ${result.height}). No charge. This does not restart the video.`);
    } catch (error) {
      setImageCheck(error instanceof Error ? error.message : "Image check failed. No charge.");
    } finally {
      setCheckingImage(false);
    }
  }

  async function retry() {
    setRetrying(true);
    setRetryError(null);
    try {
      await api.retryJob(job.id);
      onChanged();
    } catch (e) {
      setRetryError(e instanceof Error ? e.message : "Retry failed");
    } finally {
      setRetrying(false);
    }
  }

  return (
    <Card className="overflow-hidden p-0">
      <div
        className="relative w-full bg-black/40"
        style={{ aspectRatio: outputMetadata ? `${outputMetadata.width}/${outputMetadata.height}` : RATIO_CSS[job.ratio] }}
      >
        {job.status === "succeeded" && job.videoUrl ? (
          <video
            src={job.videoUrl}
            poster={job.thumbnailUrl}
            controls
            playsInline
            preload="none"
            onLoadedMetadata={(event) => {
              const video = event.currentTarget;
              if (video.videoWidth > 0 && video.videoHeight > 0) setOutputMetadata({ width: video.videoWidth, height: video.videoHeight, duration: video.duration });
            }}
            className="size-full object-contain"
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center p-5">
            {job.status === "failed" ? (
              <div className="text-center">
                <div className="text-2xl">⚠️</div>
                <div className="mt-2 text-[12px] text-[var(--danger)]">
                  {retryError ?? job.error}
                </div>
                {job.error?.includes("Provider reference image") && (
                  <div className="mt-3">
                    <Button variant="secondary" size="sm" disabled={checkingImage || retrying} onClick={() => void checkImage()}>
                      {checkingImage ? "Checking image…" : "Check existing image (no charge)"}
                    </Button>
                    {imageCheck && <p role="status" className="mt-2 text-[12px] text-muted">{imageCheck}</p>}
                  </div>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  disabled={retrying}
                  onClick={() => void retry()}
                >
                  {retrying ? (
                    <Spinner />
                  ) : (
                    `Retry (${formatUsdCents(job.chargeCents ?? MIN_VIDEO_PRICE_CENTS)}${currency !== "USD" ? " est." : ""})`
                  )}
                </Button>
              </div>
            ) : (
              <div className="w-full text-center">
                <div
                  className={cn(
                    "mx-auto mb-4 size-10 rounded-full border-2 border-white/15",
                    job.status !== "queued" &&
                      "animate-spin border-t-[var(--accent-2)]",
                  )}
                />
                <Progress value={job.progress} />
                <div className="mt-2.5 font-mono text-[12px] text-muted">
                  {job.status === "queued" && job.queuePosition
                    ? `#${job.queuePosition} in queue · ~${job.etaMinutes}m`
                    : `${job.progress}%`}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2.5 p-3.5">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-[13px] font-medium">
            {platform?.label}
          </span>
          <Badge tone={s.tone}>{s.label}</Badge>
        </div>
        <div className="flex items-center justify-between text-[11px] text-muted">
          <span>
            {avatar?.name} · Requested {job.ratio}
            {outputMetadata && ` · File ${outputMetadata.width}×${outputMetadata.height}`}
          </span>
          {(outputMetadata?.duration || job.durationSec) && (
            <span className="font-mono">{Math.round(outputMetadata?.duration || job.durationSec || 0)}s</span>
          )}
        </div>
        {job.status === "succeeded" && job.videoUrl && (
          <a
            href={`/api/jobs/${encodeURIComponent(job.id)}/download`}
            download
            className="block rounded-lg border border-line py-2 text-center text-[12px] transition hover:border-[var(--border-strong)] hover:bg-white/6"
          >
            Download MP4
          </a>
        )}
      </div>
    </Card>
  );
}
